import React from "react";
import type {
  PipeSpecification,
  ProcessSpecification,
  ProjectInformation,
} from "../models/types";

interface InputSpecificationSummaryProps {
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
  title?: string;
}

type DetailItem = { label: string; value: string | null };

const dash = (value: string | undefined) => (value?.trim() ? value.trim() : null);

const withUnit = (value: string | undefined, unit: string | undefined) => {
  const v = dash(value);
  if (!v) return null;
  return unit ? `${v} ${unit}` : v;
};

const DetailGroup = ({
  title,
  items,
}: {
  title: string;
  items: DetailItem[];
}) => {
  const visible = items.filter((item) => item.value);
  if (!visible.length) return null;

  return (
    <section className="op-input-group">
      <h4 className="op-input-group__title">{title}</h4>
      <div className="op-input-group__grid">
        {visible.map((item) => (
          <div key={item.label} className="op-input-stat">
            <span className="op-input-stat__label">{item.label}</span>
            <span className="op-input-stat__value">{item.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const InputSpecificationSummary = ({
  project,
  pipe,
  process,
  title = "Input specification",
}: InputSpecificationSummaryProps) => {
  const projectItems: DetailItem[] = [
    { label: "Project number", value: dash(project.projectNumber) },
    { label: "Project name", value: dash(project.projectName) },
    { label: "Customer", value: dash(project.customer) },
    { label: "Engineer", value: dash(project.engineer) },
    { label: "Revision", value: dash(project.projectRevision) },
    { label: "Configuration date", value: dash(project.configurationDate) },
  ];

  const pipeItems: DetailItem[] = [
    {
      label: "Outside diameter",
      value: withUnit(pipe.outsideDiameter, pipe.outsideDiameterUnit),
    },
    {
      label: "Wall thickness",
      value: withUnit(pipe.wallThickness, pipe.wallThicknessUnit),
    },
    { label: "Pipe grade", value: dash(pipe.pipeGrade) },
    { label: "Pipe length", value: withUnit(pipe.pipeLength, pipe.pipeLengthUnit) },
    { label: "Pipe tolerance", value: dash(pipe.pipeTolerance) },
    { label: "Pipe geometry", value: dash(pipe.pipeGeometry) },
  ];

  const cutbackItems: DetailItem[] = [
    {
      label: "Cutback — long side",
      value: withUnit(pipe.cutbackLongSide, pipe.cutbackLongSideUnit),
    },
    {
      label: "Cutback — short side",
      value: withUnit(pipe.cutbackShortSide, pipe.cutbackShortSideUnit),
    },
    {
      label: "Cutback + tolerance",
      value: dash(pipe.cutbackPlusTolerance),
    },
    {
      label: "Cutback − tolerance",
      value: dash(pipe.cutbackMinusTolerance),
    },
    {
      label: "Chamfer angle",
      value: dash(pipe.chamferAngleDeg) ? `${pipe.chamferAngleDeg}°` : null,
    },
    {
      label: "Chamfer + tolerance",
      value: dash(pipe.chamferPlusToleranceDeg)
        ? `${pipe.chamferPlusToleranceDeg}°`
        : null,
    },
    {
      label: "Chamfer − tolerance",
      value: dash(pipe.chamferMinusToleranceDeg)
        ? `${pipe.chamferMinusToleranceDeg}°`
        : null,
    },
  ];

  const processItems: DetailItem[] = [
    { label: "Coating type", value: dash(process.coatingType) },
    {
      label: "Coating thickness",
      value: withUnit(process.coatingThickness, process.coatingThicknessUnit),
    },
    { label: "Heating requirement", value: dash(process.heatingRequirement) },
    { label: "Blast requirement", value: dash(process.blastRequirement) },
    { label: "Surface preparation", value: dash(process.surfacePreparation) },
  ];

  return (
    <section className="op-form-card op-input-summary">
      <h3 className="op-input-summary__title">{title}</h3>
      <div className="op-input-summary__groups">
        <DetailGroup title="Project information" items={projectItems} />
        <DetailGroup title="Pipe specification" items={pipeItems} />
        <DetailGroup title="Field joint cutbacks" items={cutbackItems} />
        <DetailGroup title="Process / coating" items={processItems} />
      </div>
    </section>
  );
};

export default InputSpecificationSummary;
